import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Info } from 'lucide-react';
import { cn } from '@/utils/cn';
import { describeContext } from '@/lib/feedback-capture';
import type { CapturedContext } from '@/api/feedback.api';

/**
 * "Here is what we'll send with this."
 *
 * Shown before the user submits, never after. Collecting the page they were on,
 * the date range they had set and the filters they had applied is what makes an
 * "the numbers are wrong" report reproducible — and doing that silently, on a
 * page showing patient and revenue data, would be the wrong trade even though
 * the data never leaves the clinic's own account.
 *
 * Collapsed by default so it informs without dominating a form someone is
 * already mid-way through.
 */
export function CapturedContextPanel({
  context,
  className,
}: {
  context: CapturedContext;
  className?: string;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const rows = describeContext(context, {
    page: t('feedback.context.page'),
    dateRange: t('feedback.context.dateRange'),
    branch: t('feedback.context.branch'),
    version: t('feedback.context.version'),
    browser: t('feedback.context.browser'),
    screen: t('feedback.context.screen'),
  });

  if (rows.length === 0) return null;

  return (
    <div
      className={cn(
        'rounded-lg border border-border-subtle bg-surface-1 p-3',
        className,
      )}
    >
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        aria-expanded={expanded}
        className="flex w-full items-start gap-2 text-start outline-none focus-visible:ring-2 focus-visible:ring-primary/60 rounded-sm"
      >
        <Info aria-hidden className="mt-0.5 size-4 shrink-0 text-muted" />
        <span className="min-w-0 flex-1">
          <span className="block text-small font-medium text-foreground">
            {t('feedback.context.title')}
          </span>
          <span className="block text-micro text-muted">
            {t('feedback.context.description')}
          </span>
        </span>
        {expanded ? (
          <ChevronUp
            aria-hidden
            className="mt-0.5 size-4 shrink-0 text-muted"
          />
        ) : (
          <ChevronDown
            aria-hidden
            className="mt-0.5 size-4 shrink-0 text-muted"
          />
        )}
      </button>

      {expanded && (
        <dl className="mt-3 grid gap-x-4 gap-y-1.5 border-t border-border-subtle pt-3 sm:grid-cols-[auto_1fr]">
          {rows.map((row) => (
            <div key={`${row.label}-${row.value}`} className="contents">
              <dt className="text-micro text-muted">{row.label}</dt>
              <dd className="mb-1.5 break-words text-micro text-foreground sm:mb-0">
                {row.value}
              </dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}
